import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POPUP_STORAGE_KEY = 'agrosmart-site-popup-seen';

const SitePopup = () => {
  const { language } = useLanguage();
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timer;

    const fetchPopup = async () => {
      try {
        const res = await axios.get(`${API}/popup`);
        const data = res.data;

        if (!data?.isActive) return;

        if (
          data.showOnce &&
          localStorage.getItem(POPUP_STORAGE_KEY) === data.updatedAt
        ) {
          return;
        }

        setPopup(data);

        const delay = Number(data.delaySeconds || 0) * 1000;
        timer = setTimeout(() => setVisible(true), delay);
      } catch (error) {
        console.error('Popup fetch error:', error);
      }
    };

    fetchPopup();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const closePopup = () => {
    if (popup?.showOnce) {
      localStorage.setItem(POPUP_STORAGE_KEY, popup.updatedAt || 'seen');
    }

    setVisible(false);
  };

  const handleButtonClick = () => {
    const url = popup?.buttonUrl;

    closePopup();

    if (url) {
      window.location.href = url;
    }
  };

  if (!popup || !visible) return null;

  const title =
    language === 'ru' && popup.titleRu ? popup.titleRu : popup.title;

  const description =
    language === 'ru' && popup.descriptionRu
      ? popup.descriptionRu
      : popup.description;

  const buttonText =
    language === 'ru' && popup.buttonTextRu
      ? popup.buttonTextRu
      : popup.buttonText;

  // Imaginea în funcție de limbă: RU dacă există, altfel RO
  const popupImage =
    language === 'ru' && popup.imageRu ? popup.imageRu : popup.image;

  const popupImageSrc =
    popupImage && !/^(https?:|data:|blob:)/.test(popupImage)
      ? `${BACKEND_URL}${popupImage.startsWith('/') ? popupImage : `/${popupImage}`}`
      : popupImage;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/55 px-4 py-6">
      <div className="relative w-full max-w-[860px] overflow-hidden rounded-[28px] bg-white shadow-2xl">
        <button
          type="button"
          onClick={closePopup}
          className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-md transition hover:bg-gray-100"
          aria-label="Închide popup"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-[0.95fr_1.05fr]">
          {popupImageSrc && (
            <div className="h-[230px] bg-lime-50 md:h-full">
              <img
                src={popupImageSrc}
                alt={title}
                className="h-full w-full object-cover"
              />
            </div>
          )}

          <div className="flex flex-col justify-center p-6 md:p-9">
            <div className="mb-4 inline-flex w-fit rounded-full bg-[#a7cf26]/10 px-4 py-1.5 text-xs font-extrabold uppercase tracking-wide text-[#7fa20f]">
              AgroSmart
            </div>

            <h2 className="text-2xl font-black leading-tight text-gray-900 md:text-4xl">
              {title}
            </h2>

            {description && (
              <div
                className="mt-4 text-sm font-medium leading-relaxed text-gray-600 md:text-base"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}

            {buttonText && popup.buttonUrl && (
              <button
                type="button"
                onClick={handleButtonClick}
                className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[#a7cf26] px-6 py-3 text-sm font-extrabold text-white shadow-md shadow-lime-200 transition hover:bg-[#96bd22]"
              >
                {buttonText}
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitePopup;